import { useState } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import { categories, comics } from '@/mocks/comics';

export default function CategoryIndex() {
  const [activeTab, setActiveTab] = useState('all');

  const filteredComics = activeTab === 'all'
    ? comics
    : activeTab === 'hanman' || activeTab === 'riman' || activeTab === 'guoman'
      ? comics.filter((c) => c.category === activeTab)
      : comics.filter((c) => c.tags.some((t) => {
          const tagMap: Record<string, string> = { rexue: '热血', aiqing: '爱情', xuanyi: '悬疑', qihuan: '奇幻', gaoxiao: '搞笑' };
          return t === tagMap[activeTab];
        }));

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: '妖精动漫 - 全部分类',
    description: '妖精动漫韩漫、日漫、国漫全部分类，韩国漫画、日本漫画、国产漫画免费全集在线阅读',
    url: 'https://yaojingdongman.com/category',
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: filteredComics.map((comic, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        item: {
          '@type': 'ComicSeries',
          name: comic.title,
          url: `https://yaojingdongman.com/comic/${comic.id}`,
        },
      })),
    },
  };

  return (
    <>
      <SEOHead
        title="妖精动漫分类 | 韩漫日漫国漫分类大全 | 妖精动漫免费阅读"
        description="妖精动漫全部分类，涵盖韩漫、日漫、国漫、热血、爱情、悬疑、奇幻、搞笑等类型。韩国漫画、日本漫画、国产漫画免费全集在线阅读。"
        keywords="妖精动漫分类,韩漫,日漫,国漫,韩国漫画,日本漫画,国产漫画,热血漫画,爱情漫画,悬疑漫画,奇幻漫画,搞笑漫画"
        canonicalUrl="https://yaojingdongman.com/category"
        schema={categorySchema}
      />

      <div className="max-w-7xl mx-auto px-4 md:px-6 pt-24 pb-14">
        <Breadcrumb items={[{ label: '首页', path: '/' }, { label: '全部分类' }]} />

        <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">
          全部分类
        </h1>
        <p className="text-sm text-foreground-500 mb-8">探索所有漫画类型，找到你的最爱</p>

        {/* Category Tabs */}
        <div className="flex items-center gap-2 overflow-x-auto pb-3 mb-8 flex-wrap">
          <button
            onClick={() => setActiveTab('all')}
            className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
              activeTab === 'all'
                ? 'bg-primary-500 text-background-50'
                : 'bg-background-100 text-foreground-600 hover:bg-background-200'
            }`}
          >
            全部
          </button>
          {categories.map((cat) => (
            <button
              key={cat.slug}
              onClick={() => setActiveTab(cat.slug)}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer flex items-center gap-1.5 ${
                activeTab === cat.slug
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              <i className={`${cat.icon} text-xs`}></i>
              {cat.name}
              <span className={`text-xs ${activeTab === cat.slug ? 'text-background-50/70' : 'text-foreground-400'}`}>
                {cat.count}
              </span>
            </button>
          ))}
        </div>

        {/* Comics Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
          {filteredComics.map((comic) => (
            <Link
              key={comic.id}
              to={`/comic/${comic.id}`}
              className="group block rounded-xl overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-300 transition-all duration-300 no-underline"
            >
              <div className="relative aspect-[3/4] overflow-hidden">
                <img
                  src={comic.cover}
                  alt={comic.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  width={300}
                  height={400}
                />
                {comic.status === '已完结' && (
                  <span className="absolute top-2 right-2 px-2 py-0.5 rounded bg-accent-500/90 text-background-50 text-xs font-medium">
                    完结
                  </span>
                )}
              </div>
              <div className="p-3">
                <h3 className="text-sm font-semibold text-foreground-900 truncate group-hover:text-primary-600 transition-colors">
                  {comic.title}
                </h3>
                <p className="text-xs text-foreground-500 mt-1 truncate">{comic.author}</p>
                <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                  {comic.tags.slice(0, 2).map((tag) => (
                    <span key={tag} className="text-[10px] px-1.5 py-0.5 rounded bg-secondary-100 text-secondary-700 whitespace-nowrap">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </Link>
          ))}
        </div>

        {filteredComics.length === 0 && (
          <div className="text-center py-20">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-book-open-line text-2xl text-foreground-400"></i>
            </div>
            <p className="text-foreground-500">该分类下暂无漫画</p>
          </div>
        )}
      </div>
    </>
  );
}